import {
  type ClassIntegrationSource,
  downloadFile,
  type MadMapping,
  type MadMappingImport,
  type MadMappingItem,
  type MadOverview,
  type MadPreview,
  type MadRange,
  type MadRetraction,
  type MadSendResult,
  type MadSettings,
  type MadTransfer,
  type MadVessel,
} from '@felix/ui';
import { ApiError, api } from './api.js';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const vesselPath = (vesselId: string) => `/office/vessels/${encodeURIComponent(vesselId)}/dnv-mad`;

const rangeQuery = (range: MadRange) => {
  const query = new URLSearchParams();
  if (range.from) query.set('from', range.from);
  if (range.to) query.set('to', range.to);
  return query;
};

async function failed(response: Response): Promise<never> {
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  throw new ApiError(
    data.error ?? 'INTERNAL',
    response.status,
    typeof data.detail === 'string' ? data.detail : undefined,
    data,
  );
}

/** Dosya indirme: adı sunucunun `content-disposition`'ından. */
async function download(url: string, fallback: string, type: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`/api${url}`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!response.ok) await failed(response);
  const disposition = response.headers.get('content-disposition') ?? '';
  const fileName =
    /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1] ??
    /filename="([^"]+)"/.exec(disposition)?.[1] ??
    fallback;
  downloadFile(
    decodeURIComponent(fileName),
    new Uint8Array(await response.arrayBuffer()),
    response.headers.get('content-type') ?? type,
  );
}

/** Eşleme dosyası ham gövdeyle (CSV ya da XLSX; en çok 10 MB, kalıcı depoya yazılmaz). */
async function importMapping(
  vesselId: string,
  file: Blob,
  apply: boolean,
): Promise<MadMappingImport> {
  const isCsv = file.type === 'text/csv' || (file as File).name?.toLowerCase().endsWith('.csv');
  let response: Response;
  try {
    response = await fetch(`/api${vesselPath(vesselId)}/mapping/import?apply=${apply}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': isCsv ? 'text/csv' : XLSX },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!response.ok) await failed(response);
  return (await response.json()) as MadMappingImport;
}

/**
 * PMS ▸ Class Integration (DNV MAD; ADR 0132): ofis verisi. Anahtarlar yalnız yazılır; yanıtlarda
 * yalnız "girildi" bilgisi vardır.
 */
export const officeClassIntegrationSource: ClassIntegrationSource = {
  overview: () => api<MadOverview>('/office/dnv-mad'),
  saveSettings: (body) => api<MadSettings>('/office/dnv-mad/settings', { method: 'PUT', body }),
  saveCredentials: (body) =>
    api<MadSettings>('/office/dnv-mad/credentials', { method: 'PUT', body }),
  testConnection: () =>
    api<MadSendResult>('/office/dnv-mad/test-connection', { method: 'POST', body: {} }),
  setVessel: (vesselId, enabled, rowVersion) =>
    api<MadVessel>(`/office/dnv-mad/vessels/${encodeURIComponent(vesselId)}`, {
      method: 'PUT',
      body: { enabled, rowVersion },
    }),
  mapping: (vesselId) => api<MadMapping>(`${vesselPath(vesselId)}/mapping`),
  saveCode: (vesselId, equipmentId, body) =>
    api<{ item: MadMappingItem | null }>(
      `${vesselPath(vesselId)}/mapping/${encodeURIComponent(equipmentId)}`,
      { method: 'PUT', body },
    ),
  importMapping,
  downloadMapping: (vesselId) =>
    download(`${vesselPath(vesselId)}/mapping.xlsx`, 'DNV_MAD_Mapping.xlsx', XLSX),
  preview: (vesselId, range) => {
    const query = rangeQuery(range).toString();
    return api<MadPreview>(`${vesselPath(vesselId)}/preview${query ? `?${query}` : ''}`);
  },
  download: (vesselId, format, range) => {
    const query = rangeQuery(range);
    query.set('format', format);
    return download(
      `${vesselPath(vesselId)}/export?${query.toString()}`,
      `DNV_MAD.${format}`,
      format === 'json' ? 'application/json' : format === 'csv' ? 'text/csv' : XLSX,
    );
  },
  transfers: (vesselId) =>
    api<{ transfers: MadTransfer[]; retractions: MadRetraction[] }>(
      `${vesselPath(vesselId)}/transfers`,
    ),
  send: (vesselId, retry) =>
    api<MadTransfer>(`${vesselPath(vesselId)}/send`, { method: 'POST', body: { retry } }),
};
